import { useNavigate } from 'react-router-dom'

import { ApolloQueryResult } from '@apollo/client'
import { Box, Link, Text, Title } from '@factorialco/deprecated-design-system'
import {
  AllocationFragment,
  CurrencySelectorCurrencyFragment,
  EvaluateExpensableQuery,
  EvaluateExpensableQueryVariables,
  ExpensableBudgetFragment,
  ExpenseDetailPageQuery,
  GetExpensablesQuery,
  SpendingAlertFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { GoogleMapsProvider } from 'modules/core/components/GoogleMaps/GoogleMapsProvider'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import TimelineItem from 'modules/approvals_v2/components/TimelineItem'
import { SpendingPolicesResultData } from 'modules/expenses/graphql/types'
import {
  type CustomizationField,
  fieldEnabled,
} from 'modules/expenses/hooks/useExpensesCustomization'
import { SignableOrganizationPerCountry } from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { EXPENSABLE_CLASS } from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'

import ExpenseForm from './Expense'
import MileageForm from './Mileage'
import PerDiemForm from './PerDiem'

type ApprovalsV2ApprovalTimelineEntry = NonNullable<
  NonNullable<ExpenseDetailPageQuery['expenses']['approvalsTimelines']>[number]
>['entries'][number]

type ApprovalProcessType =
  ExpenseDetailPageQuery['expenses']['expensablesConnection']['edges'][0]['node']['approvalProcess']

interface FormProps {
  expensable: Expensable
  customizationFields: CustomizationField[]
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  adeTermsAccepted?: boolean
  onClose: () => void
  hasReportOnBehalfFeature: boolean
  hasProjectsFeature: boolean
  reimburseThroughPayrollEnabled: boolean
  reimburseThroughSepaEnabled: boolean
  refetchExpensable: () => Promise<ApolloQueryResult<GetExpensablesQuery>>
  refetchSpendingPolicyEvaluate: (
    variables: EvaluateExpensableQueryVariables
  ) => Promise<ApolloQueryResult<EvaluateExpensableQuery>>
  setSpendingPolicyEvaluationResult: (policy: SpendingPolicesResultData | undefined) => void
  canUseSpendingPolicies: boolean
  canCreateExpensesForOthers: boolean
  canOverrideApprovalFlow: boolean
  canSetExpensableToPaid: boolean
  canReviewExpensable: boolean
  canSeeEmployeeLegalEntity: boolean
  canCustomizeSettings: boolean
  currencies?: CurrencySelectorCurrencyFragment[]
  isEditable: boolean
  canDeleteExpensable: boolean
  onClickSnackBar: () => void
  toggleAlert: () => void
  hideAlert: boolean
  budget?: ExpensableBudgetFragment | null
  budgetAllocation?: AllocationFragment | null
  spendingPolicyEvaluationResult?: SpendingPolicesResultData | null
  spendingAlerts: SpendingAlertFragment[]
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
  hasWorkflowSpendingPolicies: boolean
  hasCostCentersFeature: boolean
  approvalsV2Enabled: boolean
  approvalProcess: ApprovalProcessType | null
  timelineEntries: ApprovalsV2ApprovalTimelineEntry[]
}

interface ApprovalsInformationProps {
  expensable: Expensable
}

interface ApprovalsTimelineProps {
  timelineEntries: ApprovalsV2ApprovalTimelineEntry[]
}
interface SupplementDetailProps {
  expensable: Expensable
}

const ApprovalsInformation = ({ expensable }: ApprovalsInformationProps) => (
  <Box paddingTop="s16">
    <ApprovalsInformationBlock resourceId={expensable.id} resourceType={EXPENSABLE_CLASS} />
  </Box>
)

const ApprovalsTimeline = ({ timelineEntries }: ApprovalsTimelineProps) => (
  <Box gap="s8" paddingTop="s16">
    {timelineEntries.map((entry: ApprovalsV2ApprovalTimelineEntry, index: number) => (
      <TimelineItem
        key={index}
        entry={entry}
        hasConnectingLine={index !== timelineEntries.length - 1}
        alert={undefined}
      />
    ))}
  </Box>
)

const SupplementDetail = ({ expensable }: SupplementDetailProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const supplement = expensable.supplement
  const policyPeriod = supplement?.policyPeriod

  const compensation = expensable.compensation
  const payrollRun = compensation?.payrollRun

  const periodLabel = payrollRun?.label || policyPeriod?.name

  const onSeeInPayroll = () => {
    if (!policyPeriod) return

    navigate(
      navigation.payrollBundle.policy.period.cycles.supplements.fullPath({
        policyId: policyPeriod.policyId,
        periodId: policyPeriod.id,
      }),
      { state: { supplementId: supplement.id, employeeId: expensable.owner?.id } }
    )
  }

  if (!policyPeriod) return null

  return (
    <>
      <Box paddingTop="s16">
        <Title
          variant="400"
          title={i18n.t('expenses_v2.modals.detail.reimbursement_through_payroll')}
          illustration="list-document"
        />
      </Box>

      <Box
        width="full"
        background="viridian100"
        justifyContent="center"
        alignItems="center"
        padding="s16"
        borderRadius={{ all: 'abs008' }}
      >
        <Text.Normal color="grey1000">
          {i18n.t('expenses_v2.banners.payroll_supplement', {
            policy_period_name: periodLabel,
          })}
        </Text.Normal>
      </Box>
      <Box width="full" alignItems="flexEnd">
        <Link.Touchable onClick={onSeeInPayroll}>
          {i18n.t('expenses_v2.list.actions.see_in_payroll')}
        </Link.Touchable>
      </Box>
    </>
  )
}

const Form = ({
  expensable,
  customizationFields,
  taxAgencyOrganization,
  adeTermsAccepted,
  onClose,
  hasReportOnBehalfFeature,
  hasProjectsFeature,
  reimburseThroughPayrollEnabled,
  reimburseThroughSepaEnabled,
  refetchExpensable,
  refetchSpendingPolicyEvaluate,
  setSpendingPolicyEvaluationResult,
  canUseSpendingPolicies,
  canCreateExpensesForOthers,
  canOverrideApprovalFlow,
  canSetExpensableToPaid,
  canReviewExpensable,
  canSeeEmployeeLegalEntity,
  canCustomizeSettings,
  currencies,
  isEditable,
  canDeleteExpensable,
  onClickSnackBar,
  toggleAlert,
  hideAlert,
  budget,
  budgetAllocation,
  spendingPolicyEvaluationResult,
  spendingAlerts,
  registerDropHandler,
  hasWorkflowSpendingPolicies,
  hasCostCentersFeature,
  approvalsV2Enabled,
  approvalProcess,
  timelineEntries,
}: FormProps) => {
  const approvalsInformation = approvalsV2Enabled ? (
    <ApprovalsTimeline timelineEntries={timelineEntries} />
  ) : (
    <ApprovalsInformation expensable={expensable} />
  )

  const payrollSupplementDetail = <SupplementDetail expensable={expensable} />

  const returnTypeFormComponent = () => {
    if (expensable.type === 'mileage') {
      if (!expensable.mileage) return null

      return (
        <GoogleMapsProvider
          mapsEnabled={
            fieldEnabled(customizationFields, 'mileage', 'from') ||
            fieldEnabled(customizationFields, 'mileage', 'to')
          }
        >
          <MileageForm
            expensable={expensable}
            mileage={expensable.mileage}
            customizationFields={customizationFields}
            editable={isEditable}
            approvalsInformation={approvalsInformation}
            payrollSupplement={payrollSupplementDetail}
            onClose={onClose}
            hasReportOnBehalfFeature={hasReportOnBehalfFeature}
            reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
            reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
            canCreateExpenseForOthers={canCreateExpensesForOthers}
            canOverrideApprovalFlow={canOverrideApprovalFlow}
            canSetExpensableToPaid={canSetExpensableToPaid}
            canReviewExpensable={canReviewExpensable}
            canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
            canDeleteExpensable={canDeleteExpensable}
            onClickSnackBar={onClickSnackBar}
            toggleAlert={toggleAlert}
            hideAlert={hideAlert}
            canUseSpendingPolicies={canUseSpendingPolicies}
            budget={budget}
            budgetAllocation={budgetAllocation}
            spendingPolicyEvaluationResult={spendingPolicyEvaluationResult}
            hasProjectsFeature={hasProjectsFeature}
            registerDropHandler={registerDropHandler}
            hasWorkflowSpendingPolicies={hasWorkflowSpendingPolicies}
            spendingAlerts={spendingAlerts}
            hasCostCentersFeature={hasCostCentersFeature}
            approvalsV2Enabled={approvalsV2Enabled}
            approvalProcess={approvalProcess}
          />
        </GoogleMapsProvider>
      )
    }

    if (expensable.type === 'perdiem') {
      if (!expensable.perDiem) return null

      return (
        <GoogleMapsProvider
          mapsEnabled={
            fieldEnabled(customizationFields, 'perdiem', 'from') ||
            fieldEnabled(customizationFields, 'perdiem', 'to')
          }
        >
          <PerDiemForm
            expensable={expensable}
            perDiem={expensable.perDiem}
            customizationFields={customizationFields}
            editable={isEditable}
            approvalsInformation={approvalsInformation}
            onClose={onClose}
            hasReportOnBehalfFeature={hasReportOnBehalfFeature}
            reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
            reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
            canOverrideApprovalFlow={canOverrideApprovalFlow}
            canSetExpensableToPaid={canSetExpensableToPaid}
            canReviewExpensable={canReviewExpensable}
            canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
            canDeleteExpensable={canDeleteExpensable}
            onClickSnackBar={onClickSnackBar}
            toggleAlert={toggleAlert}
            hideAlert={hideAlert}
            canUseSpendingPolicies={canUseSpendingPolicies}
            budget={budget}
            budgetAllocation={budgetAllocation}
            spendingPolicyEvaluationResult={spendingPolicyEvaluationResult}
            spendingAlerts={spendingAlerts}
            hasProjectsFeature={hasProjectsFeature}
            registerDropHandler={registerDropHandler}
            hasWorkflowSpendingPolicies={hasWorkflowSpendingPolicies}
            hasCostCentersFeature={hasCostCentersFeature}
            approvalsV2Enabled={approvalsV2Enabled}
            approvalProcess={approvalProcess}
          />
        </GoogleMapsProvider>
      )
    }

    if (!expensable.expense) return null

    return (
      <ExpenseForm
        expensable={expensable}
        expense={expensable.expense}
        customizationFields={customizationFields}
        taxAgencyOrganization={taxAgencyOrganization}
        adeTermsAccepted={adeTermsAccepted}
        editable={isEditable}
        approvalsInformation={approvalsInformation}
        payrollSupplement={payrollSupplementDetail}
        onClose={onClose}
        hasReportOnBehalfFeature={hasReportOnBehalfFeature}
        hasProjectsFeature={hasProjectsFeature}
        reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
        reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
        refetchExpensable={refetchExpensable}
        refetchSpendingPolicyEvaluate={refetchSpendingPolicyEvaluate}
        setSpendingPolicyEvaluationResult={setSpendingPolicyEvaluationResult}
        canUseSpendingPolicies={canUseSpendingPolicies}
        canCreateExpensesForOthers={canCreateExpensesForOthers}
        canOverrideApprovalFlow={canOverrideApprovalFlow}
        canSetExpensableToPaid={canSetExpensableToPaid}
        canReviewExpensable={canReviewExpensable}
        canCustomizeSettings={canCustomizeSettings}
        canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
        canDeleteExpensable={canDeleteExpensable}
        currencies={currencies}
        onClickSnackBar={onClickSnackBar}
        toggleAlert={toggleAlert}
        hideAlert={hideAlert}
        budget={budget}
        budgetAllocation={budgetAllocation}
        spendingPolicyEvaluationResult={spendingPolicyEvaluationResult}
        spendingAlerts={spendingAlerts}
        registerDropHandler={registerDropHandler}
        hasWorkflowSpendingPolicies={hasWorkflowSpendingPolicies}
        hasCostCentersFeature={hasCostCentersFeature}
        approvalsV2Enabled={approvalsV2Enabled}
        approvalProcess={approvalProcess}
      />
    )
  }

  return <>{returnTypeFormComponent()}</>
}

export default observer(Form)
